/**
 * In-Memory Token Store
 *
 * Singleton module that holds authentication tokens in memory for fast access.
 * Tokens are hydrated from NextAuth session on page load and updated on login/refresh.
 *
 * Flow:
 * 1. Login → NextAuth stores tokens in encrypted JWT cookie
 * 2. Page load → getSession() → extract tokens → setInMemoryAccessToken()
 * 3. Apollo Client reads from getInMemoryAccessToken() for Authorization header
 * 4. Token refresh → unified-client.ts updates via setInMemoryAccessToken()
 *
 * No custom cookies are created. NextAuth's encrypted cookie is the only persistence.
 */

let accessToken: string | null = null;
let refreshToken: string | null = null;

// ─── Access Token ─────────────────────────────────────────────────────────────

export function getInMemoryAccessToken(): string | null {
    return accessToken;
}

export function setInMemoryAccessToken(token: string | null): void {
    accessToken = token;
}

// ─── Refresh Token ────────────────────────────────────────────────────────────

export function getInMemoryRefreshToken(): string | null {
    return refreshToken;
}

export function setInMemoryRefreshToken(token: string | null): void {
    refreshToken = token;
}

// ─── Clear All ────────────────────────────────────────────────────────────────

export function clearInMemoryTokens(): void {
    accessToken = null;
    refreshToken = null;
}

// ─── Hydrate from NextAuth Session ────────────────────────────────────────────

/**
 * Hydrate in-memory tokens from NextAuth session.
 * Called once during app initialization (QueryProvider).
 *
 * NextAuth stores tokens in an encrypted JWT cookie.
 * We call getSession() to decrypt and extract them into memory.
 */
export async function hydrateTokensFromSession(): Promise<void> {
    if (typeof window === 'undefined') return;

    try {
        // Dynamic import to avoid pulling next-auth/react into server bundles
        const { getSession } = await import('next-auth/react');
        const session = await getSession();

        if (!session) return;

        // Identity Auth: tokens stored as commerceAccessToken/commerceRefreshToken
        const s = session as unknown as Record<string, unknown>;
        if (s.commerceAccessToken) {
            accessToken = s.commerceAccessToken as string;
        }
        if (s.commerceRefreshToken) {
            refreshToken = s.commerceRefreshToken as string;
        }

        // Legacy Auth: tokens stored in session.token
        if (!accessToken && s.token) {
            const tokenData = s.token as Record<string, unknown>;
            if (tokenData.access_token) {
                accessToken = tokenData.access_token as string;
            }
            if (tokenData.refresh_token) {
                refreshToken = tokenData.refresh_token as string;
            }
        }
    } catch (error) {
        console.error('[InMemoryTokenStore] Failed to hydrate tokens from session:', error);
    }
}
